import { Text } from "@hackthenorth/north";
import React from "react";
import styled from "styled-components";

import { ChatIcon } from "src/static/icons";
import { mediaQueries } from "src/theme/deviceBreakpoints";
import { BalanceChip } from "src/views/goose-games/components/balanceChip";
import {
  Quest,
  QuestBackgroundColorMap,
  QuestPrimaryColorMap,
  QuestType,
} from "src/views/goose-games/types";

interface Props {
  quest: Quest;
  answer: string;
  updateAnswer: (newAnswer: string) => void;
  progress?: number;
  preview?: boolean;
}

const InputQuestView = ({
  quest,
  progress = 0,
  answer,
  updateAnswer,
  preview = false,
}: Props) => {
  const completedQuest = !preview && progress >= quest.completion_limit;

  return (
    <div>
      <FlexHeading>
        <TypeContainer>
          <IconOutline type={quest.type}>
            <ChatIcon width={24} />
          </IconOutline>
          <TitleText mods="heading h3">{quest.title}</TitleText>
        </TypeContainer>
        <TypeContainer>
          {!preview && (
            <CompletionWrapper isCompleted={completedQuest}>
              <Text
                mods={
                  completedQuest ? "bold success nowrap" : "bold grey nowrap"
                }
              >
                {completedQuest
                  ? "Complete"
                  : quest.completion_limit > 1 && progress !== 0
                  ? `${progress}/${quest.completion_limit}`
                  : "Not Complete"}
              </Text>
            </CompletionWrapper>
          )}
          <BalanceChip
            isComplete={completedQuest}
            points={quest.points_rewarded}
            completionLimit={quest.completion_limit}
          />
        </TypeContainer>
      </FlexHeading>
      <ContentContainer>
        {quest.description && (
          <SubtitleText mods="p">{quest.description}</SubtitleText>
        )}
        <SubtitleText mods="bold">{"Give it a try!"}</SubtitleText>
        <DescriptionTextarea
          placeholder="Enter your answer..."
          rows={4}
          value={answer}
          onChange={(e) => updateAnswer(e.target.value)}
          disabled={completedQuest}
        />
      </ContentContainer>
    </div>
  );
};

const FlexHeading = styled.div`
  width: 50vw;
  min-width: 100%;
  max-width: 528px;
  display: flex;
  justify-content: space-between;
  padding-bottom: 24px;

  ${mediaQueries.largeMobile} {
    flex-direction: column;
    gap: 24px;
  }
`;

const IconOutline = styled.div<{ type: QuestType }>`
  background: ${({ type }) => QuestBackgroundColorMap[type]};
  color: ${({ type }) => QuestPrimaryColorMap[type]};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  border-radius: 32px;
  width: 32px;
  height: 32px;
`;

const ContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SubtitleText = styled(Text)`
  color: #6b7280 !important;
  line-height: normal !important;
  overflow: hidden;
`;

const TypeContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const DescriptionTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  background-color: ${({ disabled, theme }) =>
    disabled ? theme.globalConstants.color.backgroundGray : undefined};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : undefined)};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;

  &::placeholder {
    color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.globalConstants.color.borderPrimary};
  }
`;

const CompletionWrapper = styled.div<{ isCompleted?: boolean }>`
  display: flex;
  align-items: center;
  padding-inline: 10px;
  border-radius: 28px;
  height: 32px;
  background-color: ${({ theme, isCompleted }) =>
    isCompleted
      ? theme.globalConstants.color.greenSecondary
      : theme.globalConstants.color.backgroundGrayDark};
`;

const TitleText = styled(Text)`
  color: #374151 !important;
  line-height: normal !important;
`;

export default InputQuestView;
